import { useCallback, useSyncExternalStore } from 'react';
import { useAuthStore } from './auth.store';

// Apparence (Réglages › Apparence, 18/10/2026) : couleur du menu, « Normal » (blanc) ou « Sombre » (indigo profond).
// Choix personnel, gardé sur ce navigateur pour chaque compte (rien côté serveur) ; le menu suit aussitôt.

export type MenuTheme = 'light' | 'dark';

const keyOf = (userId?: string) => `permio:menu-theme:${userId ?? 'anonyme'}`;
// Stockage indisponible (navigation privée stricte) : le choix vaut pour la session.
const memory = new Map<string, MenuTheme>();
const listeners = new Set<() => void>();

function read(userId?: string): MenuTheme {
  const key = keyOf(userId);
  try {
    const stored = localStorage.getItem(key);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    /* lecture impossible : valeur de la session */
  }
  return memory.get(key) ?? 'light';
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Même choix dans les autres onglets ouverts.
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

export function useMenuTheme(): [MenuTheme, (theme: MenuTheme) => void] {
  const userId = useAuthStore((s) => s.user?.id);
  const theme = useSyncExternalStore(subscribe, () => read(userId));
  const setTheme = useCallback(
    (next: MenuTheme) => {
      const key = keyOf(userId);
      memory.set(key, next);
      try {
        localStorage.setItem(key, next);
      } catch {
        /* stockage indisponible : le choix vaut pour la session */
      }
      listeners.forEach((listener) => listener());
    },
    [userId],
  );
  return [theme, setTheme];
}
